import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, ClipboardList, Plus, X, AlertTriangle, Sparkles, PoundSterling } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { CANCELLATION_POLICIES, getPolicy } from '../utils/policies';
import { DepositSettingsCard } from '../components/provider/DepositSettingsCard';

const POLICY_SUGGESTED_LABEL = {
  flexible: { type: 'full', label: 'Full payment upfront (clients get refund if cancelled early)' },
  moderate: { type: 'deposit_50', label: '50% deposit, 50% paid in person' },
  strict: { type: 'full', label: 'Full payment upfront (non-refundable)' },
};

export default function PoliciesTab() {
  const [policyType, setPolicyType] = useState('flexible');
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [alignment, setAlignment] = useState(null);
  const [pendingBulk, setPendingBulk] = useState(null); // {policy, suggested}
  const [applying, setApplying] = useState(false);
  const [depositPct, setDepositPct] = useState(null);
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [applyingDeposit, setApplyingDeposit] = useState(false);
  const [pendingDepositBulk, setPendingDepositBulk] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [policyRes, rulesRes, alignRes, depositRes] = await Promise.all([
        api.get('/providers/me/cancellation-policy'),
        api.get('/providers/me/pre-appointment-rules'),
        api.get('/providers/me/policy-alignment').catch(() => ({ data: null })),
        api.get('/providers/me/deposit-default').catch(() => ({ data: null })),
      ]);
      const mapped = getPolicy(policyRes.data.type);
      setPolicyType(mapped.id);
      setRules(rulesRes.data.rules || []);
      setAlignment(alignRes.data);
      if (depositRes.data && typeof depositRes.data.default_deposit_percent === 'number') {
        setDepositPct(depositRes.data.default_deposit_percent);
      } else {
        setDepositPct(null);
      }
    } catch (err) { console.error('Failed to load policies:', err); }
    finally { setLoading(false); }
  };

  const saveDeposit = async () => {
    setSavingDeposit(true);
    try {
      await api.put('/providers/me/deposit-default', { default_deposit_percent: depositPct });
      toast.success(depositPct == null ? 'Deposit preference cleared' : `Default deposit set to ${depositPct}%`);
      const alignRes = await api.get('/providers/me/policy-alignment').catch(() => ({ data: null }));
      setAlignment(alignRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save deposit');
    } finally { setSavingDeposit(false); }
  };

  const applyDepositToAll = async () => {
    setApplyingDeposit(true);
    try {
      const res = await api.post('/providers/me/services/bulk-deposit-percent');
      const skipped = res.data?.skipped?.length || 0;
      toast.success(
        `Applied ${res.data.applied_percent}% to ${res.data.updated} service${res.data.updated === 1 ? '' : 's'}`
        + (skipped ? ` · ${skipped} skipped (below platform minimum)` : '')
      );
      setPendingDepositBulk(false);
      const alignRes = await api.get('/providers/me/policy-alignment').catch(() => ({ data: null }));
      setAlignment(alignRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk apply failed');
    } finally { setApplyingDeposit(false); }
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put('/providers/me/cancellation-policy', { type: policyType, custom_note: '' });
      toast.success('Cancellation policy saved');
      // Re-fetch alignment
      const alignRes = await api.get('/providers/me/policy-alignment').catch(() => ({ data: null }));
      setAlignment(alignRes.data);
      // Offer to apply suggested payment type if there's a meaningful mismatch
      if (alignRes.data && alignRes.data.mismatched_services.length > 0
          && alignRes.data.suggested_payment_type) {
        setPendingBulk({
          policy: policyType,
          suggested: alignRes.data.suggested_payment_type,
          count: alignRes.data.mismatched_services.length,
        });
      }
    } catch { toast.error('Failed to save policy'); }
    finally { setSavingPolicy(false); }
  };

  const applyBulk = async () => {
    if (!pendingBulk) return;
    setApplying(true);
    try {
      const res = await api.post('/providers/me/services/bulk-payment-type', {
        payment_type: pendingBulk.suggested,
      });
      toast.success(`Updated ${res.data.updated} service${res.data.updated === 1 ? '' : 's'}`);
      setPendingBulk(null);
      // Refresh alignment
      const alignRes = await api.get('/providers/me/policy-alignment').catch(() => ({ data: null }));
      setAlignment(alignRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally { setApplying(false); }
  };

  const addRule = () => {
    const trimmed = newRule.trim();
    if (!trimmed) return;
    if (rules.length >= 10) { toast.error('Maximum 10 rules'); return; }
    if (rules.includes(trimmed)) { toast.error('Rule already added'); return; }
    setRules([...rules, trimmed]);
    setNewRule('');
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.put('/providers/me/pre-appointment-rules', { rules });
      toast.success('Pre-appointment rules saved');
    } catch { toast.error('Failed to save rules'); }
    finally { setSavingRules(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" /></div>;

  return (
    <div className="space-y-6" data-testid="policies-tab">
      {/* Mismatch warning banner */}
      {alignment?.warning && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="policy-mismatch-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 mb-1">Heads up, your policy and deposit don't quite match</p>
            <p className="text-xs text-amber-800 leading-relaxed">{alignment.warning}</p>
            {alignment.suggested_payment_type && alignment.mismatched_services.length > 0 && (
              <Button
                size="sm"
                onClick={() => setPendingBulk({
                  policy: policyType,
                  suggested: alignment.suggested_payment_type,
                  count: alignment.mismatched_services.length,
                })}
                className="mt-3 btn-primary text-xs h-8 gap-1.5"
                data-testid="apply-suggested-deposit-btn"
              >
                <Sparkles className="h-3 w-3" />
                Apply suggested deposit to all services
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Cancellation Policy */}
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
            Cancellation & Refund Policy
          </h3>
          <Button onClick={savePolicy} disabled={savingPolicy} className="btn-primary" data-testid="save-policy-btn">
            {savingPolicy ? 'Saving...' : 'Save Policy'}
          </Button>
        </div>

        <p className="text-sm text-stone-500 mb-5">Choose how cancellations and deposit refunds are handled. This is shown to clients before they book.</p>

        <div className="space-y-3">
          {CANCELLATION_POLICIES.map(preset => (
            <button
              key={preset.id}
              onClick={() => setPolicyType(preset.id)}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                policyType === preset.id
                  ? 'border-[#D4AF37] bg-[#F9F6F0]'
                  : 'border-[#E6E4DD] hover:border-[#D4AF37] bg-white'
              }`}
              data-testid={`policy-${preset.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                  policyType === preset.id ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-stone-300'
                }`}>
                  {policyType === preset.id && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A] text-sm">{preset.label}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{preset.detail}</p>
                  {POLICY_SUGGESTED_LABEL[preset.id] && (
                    <p className="text-[11px] text-[#8A6D0A] mt-1.5 inline-flex items-center gap-1 bg-[#FFF8E7] px-2 py-0.5 rounded">
                      <Sparkles className="h-2.5 w-2.5" />
                      Suggested deposit: {POLICY_SUGGESTED_LABEL[preset.id].label}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-5 p-4 bg-stone-50 border border-[#E6E4DD] rounded-lg" data-testid="policy-preview">
          <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">How clients will see this</p>
          <p className="text-sm text-[#1A1A1A] font-medium">{getPolicy(policyType).label}</p>
          <p className="text-sm text-stone-600 mt-1">{getPolicy(policyType).detail}</p>
        </div>
      </div>

      {/* Default Deposit */}
      <div className="card-surface p-4 sm:p-6" data-testid="deposit-settings-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <PoundSterling className="h-5 w-5 text-[#D4AF37]" />
            Default Deposit
          </h3>
          <div className="flex flex-wrap gap-2">
            {depositPct != null && (
              <Button
                variant="outline"
                onClick={() => setPendingDepositBulk(true)}
                disabled={applyingDeposit}
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50"
                data-testid="apply-deposit-all-btn"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Apply to all services
              </Button>
            )}
            <Button onClick={saveDeposit} disabled={savingDeposit} className="btn-primary" data-testid="save-deposit-btn">
              {savingDeposit ? 'Saving...' : 'Save Deposit'}
            </Button>
          </div>
        </div>

        <DepositSettingsCard
          value={depositPct}
          onChange={setDepositPct}
          cancellationPolicy={policyType}
          showHeading={false}
          testIdPrefix="policies-deposit"
        />
      </div>

      {/* Pre-Appointment Rules */}
      <div className="card-surface p-4 sm:p-6" data-testid="pre-appointment-rules-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#D4AF37]" />
            Pre-Appointment Rules
          </h3>
          <Button onClick={saveRules} disabled={savingRules} className="btn-primary" data-testid="save-rules-btn">
            {savingRules ? 'Saving...' : 'Save Rules'}
          </Button>
        </div>

        <p className="text-sm text-stone-500 mb-5">
          Set expectations before clients arrive. These are shown before they confirm their booking. No awkward chair conversations.
        </p>

        {/* Add new rule */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newRule}
            onChange={e => setNewRule(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addRule()}
            placeholder="e.g. Come with freshly washed and blow-dried hair"
            className="input-zuriya flex-1"
            maxLength={150}
            data-testid="new-rule-input"
          />
          <Button onClick={addRule} variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50 gap-1 shrink-0" data-testid="add-rule-btn">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        {/* Rules list */}
        {rules.length > 0 ? (
          <div className="space-y-2" data-testid="rules-list">
            {rules.map((rule, index) => (
              <div key={rule} className="flex items-center gap-3 p-3 bg-[#F9F6F0] rounded-lg group" data-testid={`rule-item-${index}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                <p className="text-sm text-[#1A1A1A] flex-1">{rule}</p>
                <button
                  onClick={() => removeRule(index)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-red-500"
                  data-testid={`remove-rule-${index}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-stone-400 text-sm" data-testid="no-rules-placeholder">
            No rules added yet. Add rules like "Bring your own bundles" or "No product buildup please".
          </div>
        )}

        <p className="text-xs text-stone-400 mt-3">{rules.length}/10 rules</p>

        {/* Preview */}
        {rules.length > 0 && (
          <div className="mt-5 p-4 bg-stone-50 border border-[#E6E4DD] rounded-lg" data-testid="rules-preview">
            <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">How clients will see this</p>
            <ul className="space-y-1.5">
              {rules.map((rule) => (
                <li key={rule} className="flex items-start gap-2 text-sm text-stone-600">
                  <span className="text-[#D4AF37] mt-0.5 shrink-0">&#10003;</span>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Post-save suggestion dialog */}
      <AlertDialog open={!!pendingBulk} onOpenChange={(o) => !o && setPendingBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
              Apply suggested deposit?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulk?.policy === 'moderate'
                ? `A Moderate cancellation policy pairs best with a 50% deposit at booking, the remainder is paid in person. Apply this to all ${pendingBulk?.count ?? 0} of your services that currently differ?`
                : pendingBulk?.policy === 'strict'
                  ? `A Strict no-refund policy pairs best with full payment upfront so no balance can be disputed. Apply this to all ${pendingBulk?.count ?? 0} of your services that currently differ?`
                  : `A Flexible policy pairs best with full payment upfront (simpler for clients). Apply this to all ${pendingBulk?.count ?? 0} of your services that currently differ?`}
              <br />
              <br />
              <span className="text-xs text-stone-500">You can still override individual services later.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-skip-btn">Skip for now</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyBulk}
              disabled={applying}
              className="btn-primary"
              data-testid="bulk-apply-btn"
            >
              {applying ? 'Applying...' : 'Apply to all services'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deposit bulk-apply confirmation */}
      <AlertDialog open={pendingDepositBulk} onOpenChange={setPendingDepositBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PoundSterling className="h-5 w-5 text-[#D4AF37]" />
              Apply {depositPct}% deposit to all services?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Each active service will switch to collecting a {depositPct}% deposit upfront at the
              current price. Any custom deposit amounts will be replaced. Services where {depositPct}% falls
              below the platform fee floor will be skipped, you can set those manually afterwards.
              <br /><br />
              <span className="text-xs text-stone-500">This change is safe to reverse from the Services tab.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deposit-bulk-skip-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyDepositToAll}
              disabled={applyingDeposit}
              className="btn-primary"
              data-testid="deposit-bulk-apply-btn"
            >
              {applyingDeposit ? 'Applying...' : 'Apply to all services'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
